import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ManageOrderParams, CreateOrderResponse, Gateway } from '@/types/commerce'
import { useRecaptcha } from '@/hooks/utils/useRecaptcha'
import { useUser } from '@/hooks/users/useUser'
import { useDevice } from '@/hooks/utils/useDevice'
import { useActiveOrders } from '@/hooks/commerce/useActiveOrders'

import settings from '@/settings/public'
import { toApiError } from '@/utils/errors/toApiError'
import { extract3dsActionToken } from '@/utils/commerce'
import { QUERY_KEYS } from '@/constants/queryKeys'

/**
 * Extended response type that includes 3D Secure token for payment authentication
 * Used when payment requires additional verification step
 */
type ExtendedCreateOrderResponse = CreateOrderResponse & {
  threeDSecureActionTokenId?: string
}

/**
 * Transforms ManageOrderParams into the API request body format
 * @param args - Order management parameters
 * @param args.event - Type of order event ('create', 'cancel', 'pause', etc.)
 * @param args.product - Product object containing pricing and gateway info
 * @param args.params - Additional parameters like billing info, address, coupon code
 * @param args.params.billingToken - Payment method token from payment gateway
 * @param args.params.coupon - Promotional coupon code to apply discount
 * @param args.params.firstName - User's first name for billing
 * @param args.params.lastName - User's last name for billing
 * @param args.params.address1 - Primary address line
 * @param args.params.city - City name
 * @param args.params.country - Country code
 * @param args.profile - User profile information
 * @returns Formatted request body for the API
 */
const buildRequestParams = (args: ManageOrderParams) => {
  const { event, product, params, profile, deviceType } = args

  // Extract payment gateway info (first gateway in array is primary)
  const gateway = (product?.gateways?.[0]) as Gateway | undefined

  // Check if manual payment link (e.g., bank transfer) vs automated (e.g., credit card)
  const paymentLink = gateway?.collectionMethod === 'manual'
  const currency = gateway?.price?.currency || ''

  return {
    data: {
      billingToken: params?.billingToken,
      billingThreeDSecureToken: params?.billingThreeDSecureToken,
      productId: product?._id,
      productSKU: gateway?.sku,
      productName: product?.name?.original,
      productType: product?.type,
      gateway: {
        type: gateway?.type,
        gatewayIntegrator: gateway?.gatewayIntegrator,
        collectionType: gateway?.collectionType
      },
      currency: currency.toUpperCase(),
      userInfo: {
        code: profile?._id,
        firstName: params?.firstName,
        lastName: params?.lastName || '',
        email: profile?.email,
        phone: params?.phone,
        documentType: params?.documentType,
        documentNumber: params?.documentNumber
      },
      address: {
        street1: params?.address1,
        street2: params?.address2,
        city: params?.city,
        country: params?.country,
        postalCode: params?.postalCode
      },
      ...(params?.coupon && { coupon: params?.coupon }),
      ...(params?.acceptAddOns !== undefined && { acceptAddOns: params.acceptAddOns }),
      ...(paymentLink && { collectionMethod: gateway?.collectionMethod }),
      ...(paymentLink && { collectionType: gateway?.collectionType }),
      device: { type: deviceType || 'WEB' },
    },
    event: event,
  }
}

/**
 * Creates or manages an order via API
 * @param recaptchaToken - Google reCAPTCHA token for bot prevention
 * @param params - Order management parameters
 * @param params.event - Action: 'create', 'cancel', 'pause', 'resume', 'reactivate', 'change'
 * @param params.product - Product to purchase/manage
 * @param params.params.coupon - Optional promotional code for discounts
 * @param params.params.billingToken - Payment method token (required for paid products)
 * @param params.profile - User profile data (automatically merged)
 * @returns Promise with order data or 3D Secure token if additional auth needed
 * @throws API error object if request fails
 */
const createOrder = async ({ recaptchaToken, params }: {
  recaptchaToken: string
  params: ManageOrderParams
}): Promise<ExtendedCreateOrderResponse> => {
  const url = `${settings.api.host}/api/commerce/orders?showDetail=true`
  const requestBody = buildRequestParams(params)

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...requestBody, recaptchaToken }),
  })

  const responseJson = await response.json()

  if (responseJson.error) {
    // Check if error contains 3D Secure challenge token
    // If present, payment requires additional user authentication
    const threeDSecureActionTokenId = responseJson.error?.extra?.info?.transactionError?.threeDSecureActionTokenId

    if (threeDSecureActionTokenId) {
      return { threeDSecureActionTokenId } as ExtendedCreateOrderResponse
    }

    // Otherwise, throw the error to be handled by mutation.
    // Do NOT console.error the raw API object here — it serializes to
    // "[object Object]" as a Sentry breadcrumb. The thrown Error below is
    // already captured with a real message + stack.
    // Wrap the plain API error object in a proper Error instance so Sentry
    // captures a real Error (not "Object captured as exception with keys: …").
    // We attach all original API fields to preserve compatibility with components
    // that read error.userMessage, error.extra, etc. (e.g. UserFeedback).
    throw toApiError(responseJson.error)
  }

  return responseJson
}

/**
 * Hook for managing product purchases and active orders
 * @param props.enabled - Enable orders query (default: true)
 * @param props.retry - Retry attempts for failed queries
 * @param props.retryDelay - Delay between retries in ms
 * @returns {
 *   createOrder - Async function to create/manage orders
 *   orders - Array of active orders
 *   isPendingPurchase - True while order creation in progress
 *   isSuccessPurchase - True if last order succeeded
 *   isErrorPurchase - True if last order failed
 *   createdOrder - Data from last successful order
 *   createOrderMutation - Raw mutation object
 * }
 */
export const usePurchase = (props?: {
  enabled?: boolean,
  retry?: number,
  retryDelay?: number
}) => {
  const { enabled = true, retry, retryDelay } = props || {}
  const { withRecaptchaRetry } = useRecaptcha()
  const { profile } = useUser()
  const { isMobile } = useDevice()
  const deviceType = isMobile ? 'MOBILE_APP' : 'WEB'
  const queryClient = useQueryClient()

  const createOrderMutation = useMutation({
    mutationFn: async (params: ManageOrderParams) => {
      // The action labels the flow the token was minted for. It is reported but
      // not yet asserted (`enforceAction` ships off, until tokens from tabs
      // predating this change cycle out), so today it buys telemetry, not
      // replay protection.
      // The retry only fires when the server says the token itself was the
      // problem, and our gate rejects before anything reaches the gateway — so
      // a retried attempt cannot double-charge.
      return withRecaptchaRetry('purchase', (recaptchaToken) => (
        // Merge user profile into params and create order
        createOrder({ params: { ...params, profile, deviceType }, recaptchaToken })
      ))
    },
    onSuccess: (data) => {
      // A 3DS challenge resolves with only a threeDSecureActionTokenId and no order —
      // do not invalidate/refetch on a response that created nothing. Dual-read: with
      // the PRO-443 backend flag ON the token arrives at data.threeDSecureActionTokenId
      // instead of top level.
      if (extract3dsActionToken(data, 'purchase')) return
      // Invalidate cache to refetch with new data
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.billingInfoPrefix })
    },
  })

  // One shared cache entry for active orders (dehydrated from SSR, shared with the
  // branding-tag derivation and the post-purchase poll).
  // refetchOnMount: 'always' (FZ-WEB-NEXTJS-SJ) — without it this read the SSR-dehydrated
  // snapshot as-is on checkout mount. A subscription created moments earlier (another tab,
  // a prior session, or a just-completed purchase) wasn't reflected yet, so the "already
  // owned" guard (isSelectedProductValid in CheckoutActor/CheckoutV2Actor) stayed stale and
  // let the user fill the whole form only to have Recurly reject the duplicate subscription
  // at submit time. useOrders() right below this already uses the same flag for the same
  // reason; this query was the one still missing it.
  const ordersQuery = useActiveOrders({
    enabled,
    refetchOnMount: 'always',
    ...(retry && { retry }),
    ...(retryDelay && { retryDelay }),
  })

  return {
    ...ordersQuery,
    orders: ordersQuery.data,
    createOrder: createOrderMutation.mutateAsync,
    isPendingPurchase: createOrderMutation.isPending,
    // A 3DS challenge response resolves the mutation but creates no order — it must
    // not count as a completed purchase (guards SpecialOfferActor's success UI).
    // Dual-read for the same PRO-443 reason as the onSuccess guard above.
    isSuccessPurchase: createOrderMutation.isSuccess && !extract3dsActionToken(createOrderMutation.data, 'purchase'),
    isErrorPurchase: createOrderMutation.isError,
    createdOrder: createOrderMutation.data,
    createOrderMutation,
  }
}